import { useTranslation } from 'react-i18next';
import { CheckField, LegacyDateField, ThemeSelectField, TimeField } from 'ui/shared/controls';
import { DeviceType, ScheduleRule as _ScheduleRule, ThemeId } from 'shared/models';
import { ChangeEvent } from 'react';

// Allow themeId to be null during editing
export type ScheduleRule = Omit<_ScheduleRule, 'themeId'> & {
    themeId: ThemeId | null;
};

export type Props = {
    buildingId: string;
    disabled: boolean;
    deviceType: DeviceType;
    isPI: boolean;
    index: number;
    scheduleRule: ScheduleRule;
    onRemoveRule(): void;
    onChange(scheduleRule: Partial<ScheduleRule>): void;
};

export const ScheduleRulePanel = ({ buildingId, disabled, deviceType, isPI, index, scheduleRule, onChange, onRemoveRule }: Props) => {
    const { t } = useTranslation();

    const handleChangeCheck = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, checked } =  e.currentTarget;
        onChange({
            [name]: checked,
        });
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } =  e.currentTarget;
        onChange({
            [name]: value,
        });
    };

    const handleChangeThemeId = (themeId: ThemeId | null) => {
        onChange({
            themeId,
        });
    };

    return (
        <div style={ { marginBottom: '2em' } }>
            <div style={ { marginBottom: '1em' } }>
                <label style={ { fontSize: '1.25em' } }>
                    { index === 0 ? t('DEVICE.SCHEDULE.DEFAULT_RULE') : t('DEVICE.SCHEDULE.RULE_N', { n: (index + 1) } as any) }
                </label>
                { index > 0 &&
                    <button type="button" className="ui basic tiny icon button" onClick={ onRemoveRule } style={ { marginLeft: '1em', verticalAlign: 'middle' } }>
                        <i className="remove icon"/>
                    </button>
                }
            </div>
            <div>
                <ThemeSelectField
                    id={ `theme-${index}` }
                    labelKey="DEVICE.SCHEDULE.THEME"
                    buildingId={ buildingId }
                    deviceType={ deviceType }
                    isPI={ isPI }
                    disabled={ disabled }
                    value={ scheduleRule.themeId }
                    onChange={ handleChangeThemeId }
                />
            </div>
            { scheduleRule.themeId && index > 0 &&
                <div>
                    <div className="row">
                        <CheckField
                            id={ `from-to-date-${index}` }
                            labelKey="DEVICE.SCHEDULE.HAS_FROM_TO_DATE"
                            disabled={ disabled }
                            input={ {
                                name: 'hasFromToDate',
                                checked: !!scheduleRule.hasFromToDate,
                                onChange: handleChangeCheck,
                            } }
                        />
                    </div>
                    <div className={ `inset-row ${scheduleRule.hasFromToDate ? 'open' : ''}` }>
                        <div className="ui input">
                            <LegacyDateField
                                id={ `from-date-${index}` }
                                name="fromDate"
                                type="date"
                                labelKey="DEVICE.SCHEDULE.FROM"
                                disabled={ disabled }
                                placeholder="YYYY-MM-DD"
                                value={ scheduleRule.fromDate || '' }
                                onChange={ handleChange }
                            />
                            <LegacyDateField
                                id={ `to-date-${index}` }
                                name="toDate"
                                type="date"
                                labelKey="DEVICE.SCHEDULE.TO"
                                disabled={ disabled }
                                placeholder="YYYY-MM-DD"
                                value={ scheduleRule.toDate || '' }
                                onChange={ handleChange }
                            />
                        </div>
                    </div>

                    <div className="row">
                        <CheckField
                            id={ `days-of-week-${index}` }
                            labelKey="DEVICE.SCHEDULE.DAYS_OF_WEEK"
                            disabled={ disabled }
                            input={ {
                                name: 'hasDaysOfWeek',
                                checked: !!scheduleRule.hasDaysOfWeek,
                                onChange: handleChangeCheck,
                            } }
                        />
                    </div>
                    <div className={ `inset-row ${scheduleRule.hasDaysOfWeek ? 'open' : ''}` } style={ { lineHeight: '2.5em' } }>
                        <CheckField
                            id={ `day0-${index}` }
                            labelKey="DEVICE.SCHEDULE.DAY0"
                            disabled={ disabled }
                            input={ {
                                name: 'day0',
                                checked: !!scheduleRule.day0,
                                onChange: handleChangeCheck,
                            } }
                        />
                        <CheckField
                            id={ `day1-${index}` }
                            labelKey="DEVICE.SCHEDULE.DAY1"
                            disabled={ disabled }
                            input={ {
                                name: 'day1',
                                checked: !!scheduleRule.day1,
                                onChange: handleChangeCheck,
                            } }
                        />
                        <CheckField
                            id={ `day2-${index}` }
                            labelKey="DEVICE.SCHEDULE.DAY2"
                            disabled={ disabled }
                            input={ {
                                name: 'day2',
                                checked: !!scheduleRule.day2,
                                onChange: handleChangeCheck,
                            } }
                        />
                        <CheckField
                            id={ `day3-${index}` }
                            labelKey="DEVICE.SCHEDULE.DAY3"
                            disabled={ disabled }
                            input={ {
                                name: 'day3',
                                checked: !!scheduleRule.day3,
                                onChange: handleChangeCheck,
                            } }
                        />
                        <CheckField
                            id={ `day4-${index}` }
                            labelKey="DEVICE.SCHEDULE.DAY4"
                            disabled={ disabled }
                            input={ {
                                name: 'day4',
                                checked: !!scheduleRule.day4,
                                onChange: handleChangeCheck,
                            } }
                        />
                        <CheckField
                            id={ `day5-${index}` }
                            labelKey="DEVICE.SCHEDULE.DAY5"
                            disabled={ disabled }
                            input={ {
                                name: 'day5',
                                checked: !!scheduleRule.day5,
                                onChange: handleChangeCheck,
                            } }
                        />
                        <CheckField
                            id={ `day6-${index}` }
                            labelKey="DEVICE.SCHEDULE.DAY6"
                            disabled={ disabled }
                            input={ {
                                name: 'day6',
                                checked: !!scheduleRule.day6,
                                onChange: handleChangeCheck,
                            } }
                        />
                    </div>

                    <div className="row">
                        <CheckField
                            id={ `from-to-time-${index}` }
                            labelKey="DEVICE.SCHEDULE.HAS_FROM_TO_TIME"
                            disabled={ disabled }
                            input={ {
                                name: 'hasFromToTime',
                                checked: !!scheduleRule.hasFromToTime,
                                onChange: handleChangeCheck,
                            } }
                        />
                    </div>
                    <div className={ `inset-row ${scheduleRule.hasFromToTime ? 'open' : ''}` }>
                        <div className="ui input">
                            <TimeField
                                id={ `from-time-${index}` }
                                name="fromTime"
                                type="time"
                                labelKey="DEVICE.SCHEDULE.FROM"
                                disabled={ disabled }
                                value={ scheduleRule.fromTime || '' }
                                onChange={ handleChange }
                            />
                            <TimeField
                                id={ `to-time-${index}` }
                                name="toTime"
                                type="time"
                                labelKey="DEVICE.SCHEDULE.TO"
                                disabled={ disabled }
                                value={ scheduleRule.toTime || '' }
                                onChange={ handleChange }
                            />
                        </div>
                    </div>
                </div>
            }
        </div>
    );
};
